// components/AddVehicleModal.tsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Vehicle, PersonOfInterest, DriverLicense  } from '../types';
import mapboxgl from 'mapbox-gl';
mapboxgl.accessToken = 'pk.eyJ1IjoiY292ZXJ6b25lIiwiYSI6ImNtMG03eDYxODA2YTYya3NheW4wM25nbjcifQ.zZ6gyVv7yu7kF-gmWRL-Qw';

// mapping function for vehicle address
const mapAddressDataForVehicle = (addressData: any) => {
  
  // Try multiple possible field names from your backend
  return {
    address_line1: addressData.address_line1 || addressData.street || addressData.address_line_1 || addressData.line1 || '',
    address_line2: addressData.address_line2 || addressData.address_line_2 || addressData.line2 || '',
    suburb: addressData.suburb || addressData.state || addressData.area || '',
    city_town: addressData.city_town || addressData.city || addressData.town || '',
    postal_code: addressData.postal_code || addressData.zipCode || addressData.zip || '',
    region: addressData.region || addressData.province || addressData.state || '',
    country: addressData.country || 'South Africa'
  };
};

interface MapboxFeature {
  id: string;
  place_name: string;
  text: string;
  context?: Array<{ id: string; text: string }>;
}

interface EditableVehicle extends Vehicle {
  isEditing?: boolean;
}

interface EditableVehicleCardProps {
  vehicle: Vehicle;
  index: number;
  onEdit: (index: number) => void;
  cardNumber?: number;
  cardTitle?: string;
  showCardNumber?: boolean;
}

// Define interface for AddVehicleModal props
export interface AddVehicleModalProps {
  isVisible: boolean;
  onClose: () => void;
  onSave?: (vehicle: Vehicle) => void;
  openPersonModal: () => void;
  personsOfInterest?: PersonOfInterest[];
  clientId: number;
  quoteId?: number;
  vehicle?: Vehicle;
  mode?: 'create' | 'edit' | 'view';
  setSaveStatus?: (status: 'idle' | 'success' | 'error') => void;
  setSaveMessage?: (message: string) => void;
}

// Constants
const parkingLocations = [
  { id: '1', name: 'Garage' },
  { id: '2', name: 'Car Park' },
  { id: '3', name: 'Marked parking area' },
  { id: '4', name: 'Undercover parking' },
  { id: '5', name: 'Behind close gates' },
  { id: '6', name: 'House carport' },
  { id: '7', name: 'Security estate carport' },
  { id: '8', name: 'Street' },
  { id: '9', name: 'Driveway' },
  { id: '10', name: 'Security Complex' },
  { id: '11', name: 'Other' },
];

const coverTypes = [
  { id: '1', name: 'Comprehensive' },
  { id: '2', name: 'Third Party, Fire & Theft' },
  { id: '3', name: 'Third Party Only' },
  { id: '4', name: 'Theft Cover Excluded' },
];

const registeredOwners = [
  { id: '1', name: 'John Doe' },
  { id: '2', name: 'Jane Smith' },
  { id: '3', name: 'Company Name Inc.' },
];

const regularDrivers = [
  { id: '1', name: 'John Doe' },
  { id: '2', name: 'Jane Smith' },
  { id: '3', name: 'Person of Interest' },
];

const otherDrivers = [
  { id: '1', name: 'John Doe' },
  { id: '2', name: 'Jane Smith' },
  { id: '3', name: 'Person of Interest' },
  { id: '4', name: 'Additional Driver 1' },
  { id: '5', name: 'Additional Driver 2' },
];

const safetyFeatures = [
  { id: '1', name: 'Alarm' },
  { id: '2', name: 'Tracking Device' },
  { id: '3', name: 'Factory Fitted Alarm' },
  { id: '4', name: 'Data Dot' },
  { id: '5', name: 'Immobiliser' },
  { id: '6', name: 'Smash and Grab' },
];

const noClaimBonusOptions = [
  { id: '1', name: '12 Months  (1 Year)' },
  { id: '2', name: '24 Months  (2 Year)' },
  { id: '3', name: '36 Months  (3 Year)' },
  { id: '4', name: '48 Months  (4 Year)' },
  { id: '5', name: '60 Months  (5 Year)' },
  { id: '6', name: '72 Months  (6 Year)' },
  { id: '7', name: '84 Months  (7 Year)' },
  { id: '8', name: '96 Months  (8 Year)' },
  { id: '9', name: '108 Months  (9 Year)' },
  { id: '10', name: '120 Months  (10 Year)' },
];

export const AddVehicleModal: React.FC<AddVehicleModalProps> = ({
  isVisible,
  onClose,
  onSave,
  openPersonModal,
  personsOfInterest = [],
  clientId,
  quoteId,
  vehicle,
  mode = 'create',
  setSaveStatus,
  setSaveMessage,

}) => {

  console.log('🔍 AddVehicleModal received personsOfInterest:', personsOfInterest.map(p => ({
    name: p.name,
    surname: p.surname,
    licenses: p.drivers_license_codes
  })));

  const [clientAddress, setClientAddress] = useState({
    address_line1: '',
    address_line2: '',
    suburb: '',
    city_town: '',
    postal_code: '',
    region: '',
    country: '',
  });

  // state for client data
  const [clientData, setClientData] = useState<{
    id: number;
    first_name: string;
    last_name: string;
    drivers_license_codes?: DriverLicense[];
  } | null>(null);
  const [loadingClient, setLoadingClient] = useState(false);

  const isViewMode = mode === 'view';
  const isEditMode = mode === 'edit';
  const isCreateMode = mode === 'create';

  const formatCurrencyInput = useCallback((value: string): string => {
  if (!value) return '';
  
  // Remove any existing formatting
  const numericValue = value.replace(/[^\d.]/g, '');
  
  // Split into whole and decimal parts
  const parts = numericValue.split('.');
  let wholePart = parts[0] || '';
  let decimalPart = parts[1] || '';
  
  // Limit decimal to 2 digits
  if (decimalPart.length > 2) {
    decimalPart = decimalPart.substring(0, 2);
  }
  
  // Format the whole number part with commas
  if (wholePart) {
    wholePart = parseInt(wholePart, 10).toLocaleString('en-US');
  }
  
  // Combine parts
  return decimalPart ? `${wholePart}.${decimalPart}` : wholePart;
}, []);

const parseCurrencyInput = (value: string): number => {
  if (!value) return 0;
  // Remove commas and parse as float
  return parseFloat(value.replace(/,/g, ''));
};

// Add currency display state
const [currencyDisplay, setCurrencyDisplay] = useState({
  insuredValue: '',
  premium: '',
  excess: '',
});

  const [selectedCoverType, setSelectedCoverType] = useState('');
  const [useRetailValue, setUseRetailValue] = useState(false);
  const [excessBuyBack, setExcessBuyBack] = useState(false);
  const [creditShortfall, setCreditShortfall] = useState(false);
  const [carHire, setCarHire] = useState(false);

  const [selectedParking, setSelectedParking] = useState('');
  const [selectedNightParking, setSelectedNightParking] = useState('');
  const [selectedOwner, setSelectedOwner] = useState('');
  const [selectedDriver, setSelectedDriver] = useState('');
  const [selectedOtherDrivers, setSelectedOtherDrivers] = useState('');
  const [selectedSafetyFeatures, setSelectedSafetyFeatures] = useState('');

  const [suburb, setSuburb] = useState('');
  const [cityTown, setCityTown] = useState('');
  const [addressLine1, setAddressLine1] = useState('');
  const [addressLine2, setAddressLine2] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [region, setRegion] = useState('');
  const [country, setCountry] = useState('');

  const [overnightParkingElsewhere, setOvernightParkingElsewhere] = useState(false);
  const [overnightAddress, setOvernightAddress] = useState({
    address_line1: '',
    address_line2: '',
    suburb: '',
    city_town: '',
    postal_code: '',
    region: '',
    country: '',
  });

  // ───────────────────────────────────────────────
// Overnight Address Autocomplete (Mapbox) - same as AddressTab
// ───────────────────────────────────────────────
const [overnightSuggestions, setOvernightSuggestions] = useState<MapboxFeature[]>([]);
const [showOvernightSuggestions, setShowOvernightSuggestions] = useState(false);
const overnightInputRef = useRef<HTMLInputElement>(null);
const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

// Fetch suggestions from Mapbox
const fetchOvernightSuggestions = useCallback(async (query: string) => {
  if (query.length < 3) {
    setOvernightSuggestions([]);
    setShowOvernightSuggestions(false);
    return;
  }

  try {
    const response = await fetch(
      `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?access_token=${mapboxgl.accessToken}&types=address&country=ZA&limit=10`
    );
    const data = await response.json();
    setOvernightSuggestions(data.features || []);
    setShowOvernightSuggestions(true);
  } catch (error) {
    console.error('Error fetching overnight address suggestions:', error);
    setOvernightSuggestions([]);
    setShowOvernightSuggestions(false);
  }
}, []);

// Auto-fill when user selects a suggestion
const handleOvernightAddressSelect = (feature: MapboxFeature) => {
  let street = feature.place_name.split(',')[0].trim();
  let suburb = '';
  let city = '';
  let province = '';
  let postal = '';

  feature.context?.forEach((ctx) => {
    const ctxId = ctx.id;
    if (ctxId.startsWith('place.')) city = ctx.text;
    else if (ctxId.startsWith('locality.') || ctxId.startsWith('neighborhood.')) suburb = ctx.text;
    else if (ctxId.startsWith('region.')) province = ctx.text.replace(/\d+/g, '').trim();
    else if (ctxId.startsWith('postcode.')) postal = ctx.text;
  });

  // Fallback suburb
  if (!suburb) {
    const parts = feature.place_name.split(',');
    if (parts.length >= 2) {
      const potential = parts[1]?.trim();
      if (potential && potential !== city && !/^\d+$/.test(potential)) {
        suburb = potential;
      }
    }
  }

  // Update your existing overnightAddress state
  setOvernightAddress({
    address_line1: street,
    address_line2: '',           // can be edited later if needed
    suburb: suburb,
    city_town: city,
    postal_code: postal,
    region: province,
    country: 'South Africa',
  });

  // Optional: also update the search display field if you want to show full address
  // (you don't have a direct state for it, so we can skip or use night_parking_location if you add it)

  setShowOvernightSuggestions(false);
};

// Handle typing in the search field (no need for extra state — we just trigger search)
const handleOvernightSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  const value = e.target.value;

  // Optional: If you want to show what the user is typing in the input (live value),
  // you can add a separate state for the search display:
  // setOvernightSearchDisplay(value); // ← uncomment if you add this state below

  if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current);
  debounceTimeoutRef.current = setTimeout(() => fetchOvernightSuggestions(value), 300);
};

  const [premium, setPremium] = useState('');
  const [excess, setExcess] = useState('');
  const [selectedNoClaimBonus, setSelectedNoClaimBonus] = useState('');

  const [vehicleType, setVehicleType] = useState('');
  const [registrationNumber, setRegistrationNumber] = useState('');
  const [make, setMake] = useState('');
  const [model, setModel] = useState('');
  const [year, setYear] = useState('');
  const [variant, setVariant] = useState('');
  const [color, setColor] = useState('');
  const [usage, setUsage] = useState('');
  const [vinNumber, setVinNumber] = useState('');
  const [engineNumber, setEngineNumber] = useState('');
  const [mmcode, setMmcode] = useState('');
  const [insuredValue, setInsuredValue] = useState('');

  const [loading, setLoading] = useState(false);
  // Local state for persons of interest (used when prop is not provided)
  const [localPersonsOfInterest, setLocalPersonsOfInterest] = useState<PersonOfInterest[]>([]);
  const isLoadingEffective = loading && personsOfInterest.length === 0;

  // Use prop if provided, otherwise use local state
  const effectivePersonsOfInterest = personsOfInterest.length > 0 ? personsOfInterest : localPersonsOfInterest;
  const [error, setError] = useState('');
  const [addressLoading, setAddressLoading] = useState(false);
  const [clientError, setClientError] = useState('');

  const [localSaveMessage, setLocalSaveMessage] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const formatDriverLicenses = (licenses: DriverLicense[] | undefined) => {
    // Handle undefined, null, or empty array
    if (!licenses || !Array.isArray(licenses) || licenses.length === 0) {
      return '';
    }
    
    // Filter out licenses without codes
    const validLicenses = licenses.filter(license => {
      const hasCode = license && license.code && license.code.trim() !== '';
      return hasCode;
    });
    
    if (validLicenses.length === 0) {
      return '';
    }
    
    // Format each license: "CODE - DATE" or just "CODE" if no date
    const formattedLicenses = validLicenses.map(license => {
      const code = license.code?.trim() || '';
      const date = license.dateOfFirstIssue;
      
      // Format the date if available
      let formattedDate = '';
      if (date) {
        try {
          // Try to parse the date
          const dateObj = new Date(date);
          if (!isNaN(dateObj.getTime())) {
            // Format as DD/MM/YYYY
            formattedDate = dateObj.toLocaleDateString('en-GB', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric'
            });
          } else {
            // If date is already in a string format, use it as-is
            formattedDate = date;
          }
        } catch (error) {
          console.error('Error formatting date:', error);
          formattedDate = date;
        }
      }
      
      // Return "CODE - DATE" or just "CODE"
      return formattedDate ? `${code} - ${formattedDate}` : code;
    }).filter(formatted => formatted.trim() !== '');
    
    if (formattedLicenses.length === 0) {
      return '';
    }
    
    // Join with comma and space, and wrap in parentheses
    return ` (${formattedLicenses.join(', ')})`;
  };

  const parseLicenseData = (raw: any): DriverLicense[] => {
  if (!raw) return [];

  // ── 1. Already an array ──
  if (Array.isArray(raw)) {
    return raw
      .map((item: any) => {
        const code = String(item?.code ?? item?.license_code ?? '').trim();
        return code
          ? {
              id: item?.id,
              code,
              dateOfFirstIssue:
                item?.dateOfFirstIssue ??
                item?.date_of_first_issue ??
                item?.issue_date ??
                '',
              expiresOn:
                item?.expiresOn ?? item?.expires_on ?? item?.expiry_date ?? '',
            }
          : null;
      })
      .filter(l => !!l) as DriverLicense[];
  }

  // ── 2. String input (the problematic case right now) ──
  if (typeof raw === 'string') {
    const str = raw.trim();
    if (!str) return [];

    // Try JSON first (common in some APIs)
    try {
      const parsed = JSON.parse(str);
      return parseLicenseData(parsed); // recurse
    } catch {
      // not JSON
    }

    // ── Comma-separated pipe entries (your current format) ──
    if (str.includes(',') || str.includes('|')) {
      return str
        .split(',')
        .map((part) => part.trim())
        .filter((part) => part)
        .map((entry) => {
          const parts = entry.split('|').map((p) => p.trim());
          const code = parts[0] || '';
          const issue = parts[1] || '';
          const expiry = parts[2] || '';
          return code ? { code, dateOfFirstIssue: issue, expiresOn: expiry } : null;
        })
        .filter(l => !!l) as DriverLicense[];
    }

    // ── Plain comma-separated codes (no pipes/dates) ──
    if (str.includes(',')) {
      return str
        .split(',')
        .map((code) => code.trim())
        .filter((code) => code)
        .map((code) => ({ code, dateOfFirstIssue: '', expiresOn: '' }));
    }

    // ── Single code (no comma, no pipe) ──
    return [{ code: str, dateOfFirstIssue: '', expiresOn: '' }];
  }

  // ── 3. Single object fallback ──
  if (raw && typeof raw === 'object' && (raw.code || raw.license_code)) {
    const code = String(raw.code ?? raw.license_code ?? '').trim();
    return code
      ? [
          {
            code,
            dateOfFirstIssue: raw.dateOfFirstIssue ?? '',
            expiresOn: raw.expiresOn ?? '',
          },
        ]
      : [];
  }

  return [];
};

  const validateForm = () => {
    const errors = [];
    
    if (!vehicleType) errors.push('Vehicle Type is required');
    if (!registrationNumber) errors.push('Registration Number is required');
    if (!make) errors.push('Make is required');
    if (!model) errors.push('Model is required');
    
    // Validate numeric fields
    if (insuredValue && isNaN(parseFloat(insuredValue))) {
      errors.push('Insured Value must be a valid number');
    }
    if (premium && isNaN(parseFloat(premium))) {
      errors.push('Premium must be a valid number');
    }
    if (excess && isNaN(parseFloat(excess))) {
      errors.push('Excess must be a valid number');
    }
    if (year && (isNaN(parseInt(year)) || parseInt(year) < 1900 || parseInt(year) > new Date().getFullYear() + 1)) {
      errors.push('Year must be a valid year');
    }
    
    return errors;
  };

  const handleSaveClick = () => {
  try {
    if (isSaving) {
      console.log('⚠️ Save already in progress, skipping...');
      return;
    }
    
    setIsSaving(true);
    
    // Run validation
    const validationErrors = validateForm();
    if (validationErrors.length > 0) {
      const errorMsg = `Please fix the following errors:\n\n${validationErrors.join('\n')}`;
      if (setSaveStatus && setSaveMessage) {
        setSaveStatus('error');
        setSaveMessage(errorMsg);
      } else {
        setLocalSaveMessage(errorMsg);
      }
      setIsSaving(false);
      return;
    }

    // Prepare the vehicle data
    const vehicleData: Vehicle = {
      id: vehicle?.id || `temp-${Date.now()}`,
      vehicle_type: vehicleType,
      registration_number: registrationNumber,
      make: make,
      model: model,
      year: year ? parseInt(year) : undefined,
      variant: variant,
      color: color,
      usage: usage,
      vin_number: vinNumber,
      engine_number: engineNumber,
      mmcode: mmcode,
      insured_value: insuredValue ? parseFloat(insuredValue) : undefined,
      cover_type: selectedCoverType,
      use_retail_value: useRetailValue,
      excess_buy_back: excessBuyBack,
      credit_shortfall: creditShortfall,
      car_hire: carHire,
      day_parking_location: selectedParking,
      night_parking_location: selectedNightParking,
      registered_owner: selectedOwner,
      regular_driver: selectedDriver,
      other_drivers: selectedOtherDrivers,
      safety_features: selectedSafetyFeatures,
      address_line1: addressLine1,
      address_line2: addressLine2,
      suburb: suburb,
      city_town: cityTown,
      postal_code: postalCode,
      region: region,
      country: country,
      overnight_parking_elsewhere: overnightParkingElsewhere,
      premium: premium ? parseFloat(premium) : undefined,
      excess: excess ? parseFloat(excess) : undefined,
      no_claim_bonus: selectedNoClaimBonus,
      client_id: clientId,
      quote_id: quoteId,
      ...(overnightParkingElsewhere && {
        overnight_address_line1: overnightAddress.address_line1,
        overnight_address_line2: overnightAddress.address_line2,
        overnight_suburb: overnightAddress.suburb,
        overnight_city_town: overnightAddress.city_town,
        overnight_postal_code: overnightAddress.postal_code,
        overnight_region: overnightAddress.region,
        overnight_country: overnightAddress.country,
      })
    };
    
    console.log('🚗 Saving vehicle with ID:', vehicleData.id);
    
    // Call the onSave callback with the vehicle data
    if (onSave) {
      onSave(vehicleData);
    }
    
    // Show success message
    const successMsg = vehicle?.id ? 'Vehicle updated in quote draft!' : 'Vehicle added to quote draft!';
    if (setSaveStatus && setSaveMessage) {
      setSaveStatus('success');
      setSaveMessage(successMsg);
    } else {
      setLocalSaveMessage(successMsg);
    }
    
    // Close the modal after a short delay (to show the toast)
    setTimeout(() => {
      onClose();
    }, 1000);

  } catch (error) {
    console.error('❌ Error saving vehicle:', error);
    const errorMsg = `Failed to save vehicle: ${error instanceof Error ? error.message : 'Unknown error'}`;
    if (setSaveStatus && setSaveMessage) {
      setSaveStatus('error');
      setSaveMessage(errorMsg);
    } else {
      setLocalSaveMessage(errorMsg);
    }
  } finally {
    setIsSaving(false);
  }
};

  // fetchPersonsOfInterest function
  const fetchPersonsOfInterest = useCallback(async () => {
    if (!clientId) {
      setError('Client ID is required');
      return;
    }

    setLoading(true);
    setError('');
    try {
      await fetchClientData(clientId);

      const API_URL = process.env.REACT_APP_API_URL || '';
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API_URL}/api/persons-of-interest?clientId=${clientId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error(`Server error: ${response.status}`);
      }
      
      const data = await response.json();
      
      const mappedPersons = data.map((person: any) => {
        let drivers_license_codes: DriverLicense[] = [];

        // Check both fields
        const licenseCode = person.drivers_license_code;
        const licenseCodes = person.drivers_license_codes;
        
        // Try to use parsed data first, fall back to parsing raw data
        if (licenseCodes && Array.isArray(licenseCodes)) {
          // Use already parsed data
          drivers_license_codes = licenseCodes.map((license: any) => ({
            id: license.id || undefined,
            code: license.code || '',
            dateOfFirstIssue: license.dateOfFirstIssue || license.date_of_first_issue || '',
            expiresOn: license.expiresOn || license.expires_on || '',            
          }));
        } else if (licenseCode) {
          // Parse the raw data ourselves
          try {
            const parsed = JSON.parse(licenseCode);
            if (Array.isArray(parsed)) {
              drivers_license_codes = parsed.map((license: any) => ({
                id: license.id || undefined,
                code: license.code || '',
                dateOfFirstIssue: license.dateOfFirstIssue || license.date_of_first_issue || '',
                expiresOn: license.expiresOn || license.expires_on || '',                
              }));
            }
          } catch (error) {
            // Try comma-separated string
            if (licenseCode.includes(',')) {
              drivers_license_codes = licenseCode.split(',').map((code: string) => ({
                id: undefined,
                code: code.trim(),
                dateOfFirstIssue: '',
                expiresOn: ''             
              }));
            } else if (licenseCode.trim() !== '') {
              drivers_license_codes = [{
                id: undefined,
                code: licenseCode.trim(),
                dateOfFirstIssue: '',
                expiresOn: ''                
              }];
            }
          }
        }
        
        return {
          ...person,
          drivers_license_codes
        };
      });
      
      setLocalPersonsOfInterest(mappedPersons);
      
    } catch (err) {
      console.error('❌ Error fetching persons of interest:', err);
      setError(err instanceof Error ? err.message : 'Error loading persons of interest');
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  // Helper function to format driver licenses for display
const formatDriverLicensesForDisplay = (licenses: DriverLicense[] | undefined): string => {
  if (!licenses || !Array.isArray(licenses) || licenses.length === 0) {
    return '';
  }
  
  // Filter out licenses without codes
  const validLicenses = licenses.filter(license => {
    const hasCode = license && license.code && license.code.trim() !== '';
    return hasCode;
  });
  
  if (validLicenses.length === 0) {
    return '';
  }
  
  // Format each license: "CODE - DATE" or just "CODE" if no date
  const formattedLicenses = validLicenses.map(license => {
    const code = license.code?.trim() || '';
    const date = license.dateOfFirstIssue;
    
    let formattedDate = '';
    if (date) {
      try {
        const dateObj = new Date(date);
        if (!isNaN(dateObj.getTime())) {
          formattedDate = dateObj.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
          });
        } else {
          formattedDate = date;
        }
      } catch (error) {
        formattedDate = date;
      }
    }
    
    return formattedDate ? `${code} - ${formattedDate}` : code;
  }).filter(formatted => formatted.trim() !== '');
  
  if (formattedLicenses.length === 0) {
    return '';
  }
  
  return ` (${formattedLicenses.join(', ')})`;
};

const getAllOptions = () => {
  const options = [];
  
  // Add client as the first option if client data exists
  if (clientData) {
    const clientLicenseString = formatDriverLicensesForDisplay(clientData.drivers_license_codes || []);
    const clientOption = {
      id: clientData.id,
      name: clientData.first_name,
      surname: clientData.last_name,
      drivers_license_codes: clientData.drivers_license_codes || [],
      isClient: true,
      displayName: `${clientData.first_name} ${clientData.last_name}${clientLicenseString}`
    };
    
    options.push(clientOption);
  } else {
    // Create default if no client data
    options.push({
      id: clientId,
      name: 'Client',
      surname: `#${clientId}`,
      drivers_license_codes: [],
      isClient: true,
      displayName: `Client #${clientId}`
    });
  }
  
  // Add all persons of interest from the effective list, filtering out inactive
  effectivePersonsOfInterest.forEach(person => {
    // Skip inactive persons
    if (person.status && person.status.toLowerCase() === 'inactive') {
      return;
    }

    // Get licenses - ensure it's an array
    let licenses: DriverLicense[] = [];
    if (person.drivers_license_codes) {
      if (Array.isArray(person.drivers_license_codes)) {
        licenses = person.drivers_license_codes;
      } else if (typeof person.drivers_license_codes === 'string') {
        // Parse string if needed
        try {
          const parsed = JSON.parse(person.drivers_license_codes);
          licenses = Array.isArray(parsed) ? parsed : [];
        } catch {
          licenses = [];
        }
      }
    }
    
    // Format licenses for display
    const licenseString = formatDriverLicensesForDisplay(licenses);
    
    console.log('🔍 Person in dropdown:', {
      name: person.name,
      surname: person.surname,
      licenses: licenses,
      licenseString: licenseString
    });
    
    const personOption = {
      ...person,
      drivers_license_codes: licenses,
      isClient: false,
      displayName: `${person.name || ''} ${person.surname || ''}${licenseString}`
    };
    
    options.push(personOption);
  });
  
  return options;
};

// fetch data when add vehicle modal opens
  useEffect(() => {
    if (isVisible && clientId) {
      fetchPersonsOfInterest();
    }
  }, [isVisible, clientId, fetchPersonsOfInterest]);

useEffect(() => {
  if (clientId) {
    fetchClientData(clientId);
  }
}, [clientId]);

  // Add this useEffect to populate form when editing existing vehicle
  useEffect(() => {
    if (vehicle) {
      // Populate form fields with existing vehicle data
      setVehicleType(vehicle.vehicle_type || '');
      setRegistrationNumber(vehicle.registration_number || '');
      setMake(vehicle.make || '');
      setModel(vehicle.model || '');
      setYear(vehicle.year?.toString() || '');
      setVariant(vehicle.variant || '');
      setColor(vehicle.color || '');
      setUsage(vehicle.usage || '');
      setVinNumber(vehicle.vin_number || '');
      setEngineNumber(vehicle.engine_number || '');
      setMmcode(vehicle.mmcode || '');
      setInsuredValue(vehicle.insured_value?.toString() || '');
      setSelectedCoverType(vehicle.cover_type || '');
      setUseRetailValue(vehicle.use_retail_value || false);
      setExcessBuyBack(vehicle.excess_buy_back || false);
      setCreditShortfall(vehicle.credit_shortfall || false);
      setCarHire(vehicle.car_hire || false);
      setSelectedParking(vehicle.day_parking_location || '');
      setSelectedNightParking(vehicle.night_parking_location || '');
      setSelectedOwner(vehicle.registered_owner || '');
      setSelectedDriver(vehicle.regular_driver || '');
      setSelectedOtherDrivers(vehicle.other_drivers || '');
      setSelectedSafetyFeatures(vehicle.safety_features || '');
      setAddressLine1(vehicle.address_line1 || '');
      setAddressLine2(vehicle.address_line2 || '');
      setSuburb(vehicle.suburb || '');
      setCityTown(vehicle.city_town || '');
      setPostalCode(vehicle.postal_code || '');
      setRegion(vehicle.region || '');
      setCountry(vehicle.country || '');
      setOvernightParkingElsewhere(vehicle.overnight_parking_elsewhere || false);
      setPremium(vehicle.premium?.toString() || '');
      setExcess(vehicle.excess?.toString() || '');
      setSelectedNoClaimBonus(vehicle.no_claim_bonus || '');
      
      // Set currency display values
      setCurrencyDisplay({
        insuredValue: vehicle.insured_value ? formatCurrencyInput(vehicle.insured_value.toString()) : '',
        premium: vehicle.premium ? formatCurrencyInput(vehicle.premium.toString()) : '',
        excess: vehicle.excess ? formatCurrencyInput(vehicle.excess.toString()) : '',
      });
    }
  }, [vehicle, formatCurrencyInput]);

  const fetchClientData = async (clientId: number) => {
    setAddressLoading(true);
    setLoadingClient(true);
    setClientError('');
    
    try {
      const token = localStorage.getItem('token');
      const API_URL = process.env.REACT_APP_API_URL || '';
      
      if (!token) {
        setClientError('Authentication required. Please log in again.');
        return null;
      }
      
      // Fetch client details
      const clientResponse = await fetch(`${API_URL}/api/clients/${clientId}`, {
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (clientResponse.status === 401) {
        setClientError('Authentication failed. Please log in again.');
        localStorage.removeItem('token');
        return null;
      }

      if (!clientResponse.ok) {

        // Create default client data
        setClientData({
          id: clientId,
          first_name: 'Client',
          last_name: `#${clientId}`,
          drivers_license_codes: []
        });
      } else {
        // Success - parse the response ONCE
        const clientData = await clientResponse.json();

        // Check ALL fields that contain "license" or "driver"
        Object.keys(clientData).forEach(key => {
          if (key.toLowerCase().includes('license') || key.toLowerCase().includes('driver')) {
          }
        });

        // Parse licenses from the client data
        const parsedDriverLicenses = parseLicenseData(clientData.drivers_license_codes || clientData.drivers_license_code);
        
        // Store client data
        setClientData({
          id: clientData.id,
          first_name: clientData.name || clientData.first_name || '',
          last_name: clientData.surname || clientData.last_name || '',
          drivers_license_codes: parsedDriverLicenses
        });
      }
      
      // Fetch address separately
      const addressResponse = await fetch(`${API_URL}/api/addresses/client/${clientId}`, {
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      let addressData = null;
      if (addressResponse.ok && addressResponse.status !== 404) {
        addressData = await addressResponse.json();
      } else {
      }

      // Update the clientAddress state
      if (addressData) {
        const mappedData = mapAddressDataForVehicle(addressData);
        setClientAddress(mappedData);
      }

      return addressData;

    } catch (err) {
      console.error('fetchClientData Error:', err);
      
      // Even if there's an error, create a default client entry
      setClientData({
        id: clientId,
        first_name: 'Client',
        last_name: `#${clientId}`,
        drivers_license_codes: []
      });
      
      let errorMessage = 'Error fetching client data';
      if (err instanceof Error) {
        errorMessage = err.message;
      }
      
      setClientError(errorMessage);
      return null;
    } finally {
      setAddressLoading(false);
      setLoadingClient(false);
    }
  };

  const handleFillFromClient = async () => {
  try {
    setAddressLoading(true);

    // Use a single message state
    const message = 'Fetching client data...';
    if (setSaveMessage) {
      setSaveMessage(message);
    } else {
      setLocalSaveMessage(message);
    }
    
    setClientError(''); // Clear previous errors
    
    // Fetch client data (including address)
    const addressData = await fetchClientData(clientId);
    
    if (addressData) {
      // Get the mapped address data from the fetchClientData function
      // The function already updates clientAddress state, but we need to update form fields too
      
      // Wait a moment for state to update, then populate form fields
      setTimeout(() => {
        // Update the form fields directly from the fetched data
        const mappedData = mapAddressDataForVehicle(addressData);
        
        setAddressLine1(mappedData.address_line1 || '');
        setAddressLine2(mappedData.address_line2 || '');
        setSuburb(mappedData.suburb || '');
        setCityTown(mappedData.city_town || '');
        setPostalCode(mappedData.postal_code || '');
        setRegion(mappedData.region || '');
        setCountry(mappedData.country || '');
        
        // Show success message
        const successMessage = '✓ Client address filled successfully!';
        if (setSaveMessage) {
          setSaveMessage(successMessage);
        } else {
          setLocalSaveMessage(successMessage);
        }
        
        // Clear success message after 3 seconds
        setTimeout(() => {
          if (setSaveMessage) {
            setSaveMessage('');
          } else {
            setLocalSaveMessage('');
          }
        }, 3000);
      }, 100); // Small delay to ensure state is updated
    } else {
      // No address data found
      const warningMessage = '⚠️ No address data available for this client';
      if (setSaveMessage) {
        setSaveMessage(warningMessage);
      } else {
        setLocalSaveMessage(warningMessage);
      }
    }

  } catch (error) {
    console.error('❌ Error in handleFillFromClient:', error);
    const errorMessage = '❌ Failed to fetch address. Please try again.';
    
    if (setSaveMessage) {
      setSaveMessage(errorMessage);
    } else {
      setLocalSaveMessage(errorMessage);
    }
    
    setTimeout(() => {
      if (setSaveMessage) {
        setSaveMessage('');
      } else {
        setLocalSaveMessage('');
      }
    }, 5000);
  } finally {
    setAddressLoading(false);
  }
};

  if (!isVisible) return null;

  const handleCoverTypeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedCoverType(event.target.value);
  };

  const handleRetailValueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setUseRetailValue(event.target.checked);
  };

  const handleExcessBuyBackChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setExcessBuyBack(event.target.checked);
  };

  const handleCreditShortfallChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCreditShortfall(event.target.checked);
  };

  const handleCarHireChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCarHire(event.target.checked);
  };

  const handleParkingChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedParking(event.target.value);
  };

  const handleNightParkingChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedNightParking(event.target.value);
  };

  const handleOwnerChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedOwner(event.target.value);
  };

  const handleDriverChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedDriver(event.target.value);
  };

  const handleOtherDriversChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedOtherDrivers(event.target.value);
  };

  const handleSafetyFeaturesChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedSafetyFeatures(event.target.value);
  };

  const handleAddressLine1Change = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAddressLine1(event.target.value);
  };

  const handleAddressLine2Change = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAddressLine2(event.target.value);
  };

  const handleSuburbChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSuburb(event.target.value);
  };

  const handleCityTownChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCityTown(event.target.value);
  };

  const handlePostalCodeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPostalCode(event.target.value);
  };

  const handleRegionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRegion(event.target.value);
  };

  const handleCountryChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCountry(event.target.value);
  };

  const handleOvernightParkingChange = (event: React.ChangeEvent<HTMLInputElement>) => {
  const checked = event.target.checked;
  setOvernightParkingElsewhere(checked);

  // Always clear the overnight address when checkbox is toggled
  // User must enter the address manually
  setOvernightAddress({
    address_line1: '',
    address_line2: '',
    suburb: '',
    city_town: '',
    postal_code: '',
    region: '',
    country: '',
  });
};

  const handleOvernightAddressChange = (field: keyof typeof overnightAddress, value: string) => {
  setOvernightAddress(prev => ({
    ...prev,
    [field]: value,
  }));
};


  const handlePremiumChange = (event: React.ChangeEvent<HTMLInputElement>) => {
  const value = event.target.value;
  const formattedValue = formatCurrencyInput(value);
  const numericValue = parseCurrencyInput(value);
  
  setPremium(numericValue ? numericValue.toString() : '');
  setCurrencyDisplay(prev => ({
    ...prev,
    premium: formattedValue,
  }));
};

  const handleExcessChange = (event: React.ChangeEvent<HTMLInputElement>) => {
  const value = event.target.value;
  const formattedValue = formatCurrencyInput(value);
  const numericValue = parseCurrencyInput(value);
  
  setExcess(numericValue ? numericValue.toString() : '');
  setCurrencyDisplay(prev => ({
    ...prev,
    excess: formattedValue,
  }));
};

const handleCurrencyBlur = (field: 'insuredValue' | 'premium' | 'excess') => {
  const currentValue = currencyDisplay[field];
  if (currentValue) {
    const numericValue = parseCurrencyInput(currentValue);
    if (numericValue > 0) {
      const formatted = formatCurrencyInput(numericValue.toFixed(2));
      setCurrencyDisplay(prev => ({ ...prev, [field]: formatted }));
    }
  }
};

  const handleNoClaimBonusChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedNoClaimBonus(event.target.value);
  };

  const handleVehicleTypeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setVehicleType(event.target.value);
  };

  const handleRegistrationNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRegistrationNumber(event.target.value);
  };

  const handleMakeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setMake(event.target.value);
  };

  const handleModelChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setModel(event.target.value);
  };

  const handleYearChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setYear(event.target.value);
  };

  const handleVariantChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setVariant(event.target.value);
  };

  const handleColorChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setColor(event.target.value);
  };

  const handleUsageChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setUsage(event.target.value);
  };

  const handleVinNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setVinNumber(event.target.value);
  };

  const handleEngineNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEngineNumber(event.target.value);
  };

  const handleMmcodeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setMmcode(event.target.value);
  };

  const handleInsuredValueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
  const value = event.target.value;
  const formattedValue = formatCurrencyInput(value);
  const numericValue = parseCurrencyInput(value);
  
  setInsuredValue(numericValue ? numericValue.toString() : '');
  setCurrencyDisplay(prev => ({
    ...prev,
    insuredValue: formattedValue,
  }));
};

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex justify-center items-center z-50">
      <div className="relative bg-white p-10 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <h2 className="text-2xl font-bold mb-6">
          {isViewMode ? 'View Vehicle Details' : 
           isEditMode ? 'Edit Vehicle Details' : 'Add Vehicle Details'}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          {/* Vehicle Type */}
          <div>
            <label htmlFor="vehicleType" className="block text-gray-700 font-bold mb-2 text-sm">
              Vehicle Type
            </label>
            {isViewMode ? (
              <div className="p-2 border rounded bg-gray-50 text-gray-700">
                {vehicleType || 'Not specified'}
              </div>
            ) : (
            <select
              id="vehicleType"
              value={vehicleType}
              onChange={handleVehicleTypeChange}
              className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
            >
              <option value="">Select</option>
              <option value="car">Car</option>
              <option value="motorcycle">Motorcycle</option>
              <option value="LDV<1400">LDV greater than 1400</option>
              <option value="LDV>1400">LDV less than 1400</option>
              <option value="4X2 Double Cap">4X2 Double Cap</option>
              <option value="4X2 Single Cap">4X2 Single Cap</option>
              <option value="4X4 Double cab">4X4 Double cab</option>
              <option value="4X4 Single Cap">4X4 Single Cap</option>
              <option value="4X4 Luxuty">4X4 Luxuty</option>
              <option value="Kombi">Kombi</option>
              <option value="Venture">Venture</option>
              <option value="SUV">SUV</option>
              <option value="Other">Other</option>
            </select>
            )}
          </div>
          
          {/* Registration Number */}
          <div>
            <label htmlFor="registrationNumber" className="block text-gray-700 font-bold mb-2 text-sm">
              Reg. Number
            </label>
            {isViewMode ? (
              <div className="p-2 border rounded bg-gray-50 text-gray-700">
                {registrationNumber || 'Not specified'}
              </div>
            ) : (
              <input
                type="text"
                id="registrationNumber"
                value={registrationNumber}
                onChange={handleRegistrationNumberChange}
                className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                placeholder="e.g., ABC 123 GP"
                disabled={isViewMode}
              />
            )}
          </div>
          
          {/* Make */}
          <div>
            <label htmlFor="make" className="block text-gray-700 font-bold mb-2 text-sm">
              Make
            </label>
            {isViewMode ? (
              <div className="p-2 border rounded bg-gray-50 text-gray-700">
                {make || 'Not specified'}
              </div>
            ) : (
            <input
              type="text"
              id="make"
              value={make}
              onChange={handleMakeChange}
              className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
              placeholder="e.g., Toyota"
            />
            )}
          </div>
          
          {/* Model */}
          <div>
            <label htmlFor="model" className="block text-gray-700 font-bold mb-2 text-sm">
              Model
            </label>
            {isViewMode ? (
              <div className="p-2 border rounded bg-gray-50 text-gray-700">
                {model || 'Not specified'}
              </div>
            ) : (
            <input
              type="text"
              id="model"
              value={model}
              onChange={handleModelChange}
              className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
              placeholder="e.g., Corolla"
            />
            )}
          </div>
          
          {/* Year */}
          <div>
            <label htmlFor="year" className="block text-gray-700 font-bold mb-2 text-sm">
              Year
            </label>
            {isViewMode ? (
              <div className="p-2 border rounded bg-gray-50 text-gray-700">
                {year || 'Not specified'}
              </div>
            ) : (
            <input
              type="text"
              id="year"
              value={year}
              onChange={handleYearChange}
              className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
              placeholder="e.g., 2022"
            />
            )}
          </div>
          
          {/* Variant */}
          <div>
            <label htmlFor="vehicleVariant" className="block text-gray-700 font-bold mb-2 text-sm">
              Variant
            </label>
            {isViewMode ? (
              <div className="p-2 border rounded bg-gray-50 text-gray-700">
                {variant || 'Not specified'}
              </div>
            ) : (
            <input
              type="text"
              id="vehicleVariant"
              value={variant}
              onChange={handleVariantChange}
              className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
              placeholder="e.g., 1.8L"
            />
            )}
          </div>
          
          {/* Color */}
          <div>
            <label htmlFor="vehicleColor" className="block text-gray-700 font-bold mb-2 text-sm">
              Color
            </label>
            {isViewMode ? (
              <div className="p-2 border rounded bg-gray-50 text-gray-700">
                {color || 'Not specified'}
              </div>
            ) : (
            <input
              type="text"
              id="vehicleColor"
              value={color}
              onChange={handleColorChange}
              className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
              placeholder="e.g., Blue"
            />
            )}
          </div>
          
          {/* Vehicle Usage */}
          <div>
            <label htmlFor="vehicleUsage" className="block text-gray-700 font-bold mb-2 text-sm">
              Vehicle Usage
            </label>
            {isViewMode ? (
              <div className="p-2 border rounded bg-gray-50 text-gray-700">
                {usage || 'Not specified'}
              </div>
            ) : (
            <select
              id="vehicleUsage"
              value={usage}
              onChange={handleUsageChange}
              className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
              required
            >
              <option value="">Select Usage</option>
              <option value="Business">Business</option>
              <option value="Private">Private</option>
              <option value="Combined">Combined</option>
            </select>
            )}
          </div>
          
          {/* VIN Number */}
          <div>
            <label htmlFor="vinNumber" className="block text-gray-700 font-bold mb-2 text-sm">
              VIN Number
            </label>
            {isViewMode ? (
              <div className="p-2 border rounded bg-gray-50 text-gray-700">
                {vinNumber || 'Not specified'}
              </div>
            ) : (
            <input
              type="text"
              id="vinNumber"
              value={vinNumber}
              onChange={handleVinNumberChange}
              className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
              placeholder="e.g., JT2FA22D1X06xxxxx"
            />
            )}
          </div>
          
          {/* Engine Number */}
          <div>
            <label htmlFor="engineNumber" className="block text-gray-700 font-bold mb-2 text-sm">
              Engine Number
            </label>
            {isViewMode ? (
              <div className="p-2 border rounded bg-gray-50 text-gray-700">
                {engineNumber || 'Not specified'}
              </div>
            ) : (
            <input
              type="text"
              id="engineNumber"
              value={engineNumber}
              onChange={handleEngineNumberChange}
              className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
              placeholder="e.g., 1KRFE08xxxxx"
            />
            )}
          </div>
          
          {/* MMCode */}
          <div>
            <label htmlFor="mmcode" className="block text-gray-700 font-bold mb-2 text-sm">
              MMCode
            </label>
            {isViewMode ? (
              <div className="p-2 border rounded bg-gray-50 text-gray-700">
                {mmcode || 'Not specified'}
              </div>
            ) : (
            <input
              type="text"
              id="mmcode"
              value={mmcode}
              onChange={handleMmcodeChange}
              className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
              placeholder="e.g., MM00123"
            />
            )}
          </div>
          
          {/* Cover Type */}
          <div>
            <label htmlFor="coverType" className="block text-gray-700 font-bold mb-2 text-sm">
              Cover Type
            </label>
            {isViewMode ? (
            <div className="p-2 border rounded bg-gray-50 text-gray-700">
              {selectedCoverType || 'Not specified'}
            </div>
          ) : (
            <select
              id="coverType"
              name="coverType"
              value={selectedCoverType}
              onChange={handleCoverTypeChange}
              className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
            >
              <option value="" disabled>Select</option>
              {coverTypes.map(cover => (
                <option key={cover.id} value={cover.id}>
                  {cover.name}
                </option>
              ))}
            </select>
            )}
          </div>

          {/* Empty div to maintain grid layout */}
          <div></div>
        </div>
          
          {/* Insured Value and No Claim Bonus - Using flex for equal spacing */}
        <div className="flex space-x-6 mb-6">
          {/* Insured Value */}
          <div className="flex-1">
            <label htmlFor="insuredValue" className="block text-gray-700 font-bold mb-2 text-sm">
              Insured Value <span className="font-bold italic text-gray-600">(Rand Value Only)</span>
            </label>
            {isViewMode ? (
              <div className="p-2 border rounded bg-gray-50 text-gray-700">
                {insuredValue || 'Not specified'}
              </div>
            ) : (
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <span className="text-gray-500">R</span>
              </div>
              <input
                type="text"
                id="insuredValue"
                value={currencyDisplay.insuredValue}
                onChange={handleInsuredValueChange}
                onBlur={() => handleCurrencyBlur('insuredValue')}
                className="shadow-sm border rounded w-full py-2 px-3 pl-8 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                placeholder="0.00"
              />
            </div>
            )}
          </div>
          
          {/* No Claim Bonus */}
          <div className="flex-1">
            <label htmlFor="noClaimBonus" className="block text-gray-700 font-bold mb-2 text-sm">
              No Claim Bonus
            </label>
            {isViewMode ? (
              <div className="p-2 border rounded bg-gray-50 text-gray-700">
                {selectedNoClaimBonus || 'Not specified'}
              </div>
            ) : (
            <select
              id="noClaimBonus"
              name="noClaimBonus"
              value={selectedNoClaimBonus}
              onChange={handleNoClaimBonusChange}
              className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
            >
              <option value="" disabled>Select bonus</option>
              {noClaimBonusOptions.map(option => (
                <option key={option.id} value={option.id}>
                  {option.name}
                </option>
              ))}
            </select>
            )}
          </div>
        </div>

        {/* Cover Type Checkboxes */}
        {/* Use Retail Value Checkbox */}
        <div className="mt-4 mb-6">
          <div className="flex items-center space-x-4">
            <div className="flex items-center">
              {isViewMode ? (
                <div className={`inline-block w-4 h-4 rounded ${useRetailValue ? 'bg-blue-600' : 'bg-gray-300 border border-gray-400'}`}></div>
              ) : (
                <input
                  type="checkbox"
                  id="useRetailValue"
                  name="useRetailValue"
                  checked={useRetailValue}
                  onChange={handleRetailValueChange}
                  className="form-checkbox h-4 w-4 text-blue-600 rounded"
                />
              )}
              <label htmlFor="useRetailValue" className="ml-2 text-gray-700 text-sm whitespace-nowrap">Use retail value</label>
            </div>

            {/* Excess Buy Back Checkbox */}
            <div className="flex items-center">
              {isViewMode ? (
                <div className={`inline-block w-4 h-4 rounded ${excessBuyBack ? 'bg-blue-600' : 'bg-gray-300 border border-gray-400'}`}></div>
              ) : (
                <input
                  type="checkbox"
                  id="excessBuyBack"
                  name="excessBuyBack"
                  checked={excessBuyBack}
                  onChange={handleExcessBuyBackChange}
                  className="form-checkbox h-4 w-4 text-blue-600 rounded"
                />
              )}
              <label htmlFor="excessBuyBack" className="ml-2 text-gray-700 text-sm whitespace-nowrap">Excess Buy Back</label>
            </div>

            {/* Credit Shortfall Checkbox */}
            <div className="flex items-center">
              {isViewMode ? (
                <div className={`inline-block w-4 h-4 rounded ${creditShortfall ? 'bg-blue-600' : 'bg-gray-300 border border-gray-400'}`}></div>
              ) : (
                <input
                  type="checkbox"
                  id="creditShortfall"
                  name="creditShortfall"
                  checked={creditShortfall}
                  onChange={handleCreditShortfallChange}
                  className="form-checkbox h-4 w-4 text-blue-600 rounded"
                />
              )}
              <label htmlFor="creditShortfall" className="ml-2 text-gray-700 text-sm whitespace-nowrap">Credit Shortfall</label>
            </div>

            {/* Car Hire Checkbox */}
            <div className="flex items-center">
              {isViewMode ? (
                <div className={`inline-block w-4 h-4 rounded ${carHire ? 'bg-blue-600' : 'bg-gray-300 border border-gray-400'}`}></div>
              ) : (
                <input
                  type="checkbox"
                  id="carHire"
                  name="carHire"
                  checked={carHire}
                  onChange={handleCarHireChange}
                  className="form-checkbox h-4 w-4 text-blue-600 rounded"
                />
              )}
              <label htmlFor="carHire" className="ml-2 text-gray-700 text-sm whitespace-nowrap">Car Hire</label>
            </div>

          </div>
        </div>

        {/* Parking Selection */}
        <div className="flex space-x-4 mt-6 mb-6">
          {/* Parking during the day */}
          <div className="flex-1">
            <label htmlFor="parkingLocation" className="block text-gray-700 font-bold mb-2 text-sm">
              Select parking locations for during the day
            </label>
            {isViewMode ? (
              <div className="p-2 border rounded bg-gray-50 text-gray-700">
                {selectedParking || 'Not specified'}
              </div>
            ) : (
            <select
              id="parkingLocation"
              name="parkingLocation"
              value={selectedParking}
              onChange={handleParkingChange}
              className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
            >
              <option value="" disabled>Select a location</option>
              {parkingLocations.map(location => (
                <option key={location.id} value={location.id}>
                  {location.name}
                </option>
              ))}
            </select>
            )}
          </div>

          {/* Parking during the night */}
          <div className="flex-1">
            <label htmlFor="parkingLocationNight" className="block text-gray-700 font-bold mb-2 text-sm">
              Select parking locations for during the night
            </label>
            {isViewMode ? (
              <div className="p-2 border rounded bg-gray-50 text-gray-700">
                {selectedNightParking || 'Not specified'}
              </div>
            ) : (
            <select
              id="parkingLocationNight"
              name="parkingLocationNight"
              value={selectedNightParking}
              onChange={handleNightParkingChange}
              className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
            >
              <option value="" disabled>Select a location</option>
              {parkingLocations.map(location => (
                <option key={location.id} value={location.id}>
                  {location.name}
                </option>
              ))}
            </select>
            )}
          </div>
        </div>

        {/* Person of Interest Buttons */}
        <div className="mb-6 flex space-x-4">
          <button
            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-lg shadow-md transition duration-300 ease-in-out transform hover:scale-105"
            onClick={openPersonModal}
          >
            Create person of interest
          </button>
          
        </div>

        {!clientId && (
          <div className="mb-4 p-3 bg-yellow-100 border border-yellow-400 text-yellow-700 rounded">
            Client ID is required to load persons of interest
          </div>
        )}

        {error && (
          <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
            {error}
          </div>
        )}

        {isLoadingEffective && (
          <div className="mb-4 p-3 bg-blue-100 border border-blue-400 text-blue-700 rounded">
            Loading persons of interest...
          </div>
        )}

        <div className="flex space-x-4 mb-6">
          {/* Registered Owner dropdown */}
          <div className="flex-1">
            <label htmlFor="registeredOwner" className="block text-gray-700 font-bold mb-2 text-sm">
              Registered Owner
            </label>
            {isViewMode ? (
              <div className="p-2 border rounded bg-gray-50 text-gray-700">
                {selectedOwner || 'Not specified'}
              </div>
            ) : (
              <select
                id="registeredOwner"
                name="registeredOwner"
                value={selectedOwner}
                onChange={handleOwnerChange}
                className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                disabled={loading || !clientId}
              >
                <option value="" disabled>Select a person</option>
                {getAllOptions().length > 0 ? (
                  getAllOptions().map(option => {
                    const clientTag = option.isClient ? ' (Client)' : '';
                    
                    return (
                      <option 
                        key={option.isClient ? `client-${option.id}` : option.id?.toString() || ''} 
                        value={option.isClient ? `client-${option.id}` : option.id?.toString() || ''}
                      >
                        {option.displayName}{clientTag}
                      </option>
                    );
                  })
                ) : (
                  <option value="" disabled>No options available</option>
                )}
              </select>
            )}
          </div>
          
          {/* Regular Driver dropdown */}
          <div className="flex-1">
            <label htmlFor="regularDriver" className="block text-gray-700 font-bold mb-2 text-sm">
              Regular Driver
            </label>
            {isViewMode ? (
              <div className="p-2 border rounded bg-gray-50 text-gray-700">
                {selectedDriver || 'Not specified'}
              </div>
            ) : (
            <select
              id="regularDriver"
              name="regularDriver"
              value={selectedDriver}
              onChange={handleDriverChange}
              className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
              disabled={loading || !clientId}
            >
              <option value="" disabled>Select a driver</option>
              {getAllOptions().length > 0 ? (
                getAllOptions().map(option => {
                  const clientTag = option.isClient ? ' (Client)' : '';
                  
                  return (
                    <option 
                      key={option.isClient ? `client-${option.id}` : option.id?.toString() || ''} 
                      value={option.isClient ? `client-${option.id}` : option.id?.toString() || ''}
                    >
                      {option.displayName}{clientTag}
                    </option>
                  );
                })
              ) : (
                <option value="" disabled>No options available</option>
              )}
            </select>
            )}
          </div>
        </div>

        <div className="flex space-x-4 mb-6">
          {/* Other Drivers dropdown */}
          <div className="flex-1">
            <label htmlFor="otherDrivers" className="block text-gray-700 font-bold mb-2 text-sm">
              Other Drivers
            </label>
            {isViewMode ? (
              <div className="p-2 border rounded bg-gray-50 text-gray-700">
                {selectedOtherDrivers || 'Not specified'}
              </div>
            ) : (
            <select
              id="otherDrivers"
              name="otherDrivers"
              value={selectedOtherDrivers}
              onChange={handleOtherDriversChange}
              className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
              disabled={loading || !clientId}
            >
              <option value="" disabled>Select other drivers</option>
              {getAllOptions().length > 0 ? (
                getAllOptions().map(option => {
                  const clientTag = option.isClient ? ' (Client)' : '';
                  
                  return (
                    <option 
                      key={option.isClient ? `client-${option.id}` : option.id?.toString() || ''} 
                      value={option.isClient ? `client-${option.id}` : option.id?.toString() || ''}
                    >
                      {option.displayName}{clientTag}
                    </option>
                  );
                })
              ) : (
                <option value="" disabled>No options available</option>
              )}
            </select>
            )}
          </div>
          
          {/* Safety Features dropdown */}
          <div className="flex-1">
            <label htmlFor="safetyFeatures" className="block text-gray-700 font-bold mb-2 text-sm">
              Safety Features
            </label>
            {isViewMode ? (
              <div className="p-2 border rounded bg-gray-50 text-gray-700">
                {selectedSafetyFeatures || 'Not specified'}
              </div>
            ) : (
            <select
              id="safetyFeatures"
              name="safetyFeatures"
              value={selectedSafetyFeatures}
              onChange={handleSafetyFeaturesChange}
              className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
            >
              <option value="" disabled>Select safety features</option>
              {safetyFeatures.map(feature => (
                <option key={feature.id} value={feature.id}>
                  {feature.name}
                </option>
              ))}
            </select>
            )}
          </div>
        </div>

        {/* Fill from Client Button and Status */}
        <div className="mb-6">

          <button
            className={`${addressLoading ? 'bg-gray-400' : 'bg-green-500 hover:bg-green-700'} text-white font-bold py-2 px-4 rounded-lg shadow-md transition duration-300 ${addressLoading ? '' : 'transform hover:scale-105'} w-full flex items-center justify-center`}
            onClick={handleFillFromClient}
            disabled={addressLoading || isViewMode}
          >
            {addressLoading ? (
              <>
                <span className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></span>
                Fetching Address...
              </>
            ) : (
              'Fill from Client'
            )}
          </button>
          
          {/* Success/Error Message */}
          {localSaveMessage && (
            <div className={`mt-2 p-2 text-sm rounded border ${
              localSaveMessage.includes('❌') 
                ? 'bg-red-100 border-red-400 text-red-700' 
                : 'bg-green-100 border-green-400 text-green-700'
            }`}>
              {localSaveMessage}
            </div>
          )}
          
          {/* Client Error Message (from fetchClientAddress) */}
          {clientError && !localSaveMessage && (
            <div className="mt-2 p-2 bg-red-100 border border-red-400 text-red-700 text-sm rounded">
              {clientError}
            </div>
          )}
          
          {/* No Address Available Message */}
          {!clientError && !clientAddress.address_line1 && !addressLoading && !localSaveMessage && (  // CHANGE: !saveMessage -> !localSaveMessage
            <div className="mt-2 p-2 bg-yellow-100 border border-yellow-400 text-yellow-700 text-sm rounded">
              Note: No address data available for this client yet
            </div>
          )}
        </div>

        {/* Simple status message */}
        {!clientAddress.address_line1 && (
          <div className="mb-4 p-2 bg-yellow-100 text-yellow-700 text-sm rounded">
            Note: No address data available for this client yet
          </div>
        )}

        {/* Address Fields */}
        <div className="flex space-x-4 mb-6">
          {/* Address Line 1 */}
          <div className="flex-1">
            <label htmlFor="addressLine1" className="block text-gray-700 font-bold mb-2 text-sm">
              Address Line 1
            </label>
            {isViewMode ? (
              <div className="p-2 border rounded bg-gray-50 text-gray-700">
                {addressLine1 || 'Not specified'}
              </div>
            ) : (
            <input
              type="text"
              id="addressLine1"
              value={addressLine1}
              onChange={handleAddressLine1Change}
              className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
              placeholder="e.g., 123 Main Street"
            />
            )}
          </div>
          
          {/* Address Line 2 */}
          <div className="flex-1">
            <label htmlFor="addressLine2" className="block text-gray-700 font-bold mb-2 text-sm">
              Address Line 2
            </label>
            {isViewMode ? (
              <div className="p-2 border rounded bg-gray-50 text-gray-700">
                {addressLine2 || 'Not specified'}
              </div>
            ) : (
            <input
              type="text"
              id="addressLine2"
              value={addressLine2}
              onChange={handleAddressLine2Change}
              className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
              placeholder="e.g., Apt 4B, Suburb"
            />
            )}
          </div>
        </div>

        {/* Suburb and City Fields */}
        <div className="flex space-x-4 mb-6">
          {/* Suburb */}
          <div className="flex-1">
            <label htmlFor="suburb" className="block text-gray-700 font-bold mb-2 text-sm">
              Suburb
            </label>
            {isViewMode ? (
              <div className="p-2 border rounded bg-gray-50 text-gray-700">
                {suburb || 'Not specified'}
              </div>
            ) : (
            <input
              type="text"
              id="suburb"
              value={suburb}
              onChange={handleSuburbChange}
              className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
              placeholder="e.g., Sandton"
            />
            )}
          </div>
          
          {/* City/Town */}
          <div className="flex-1">
            <label htmlFor="cityTown" className="block text-gray-700 font-bold mb-2 text-sm">
              City/Town
            </label>
            {isViewMode ? (
              <div className="p-2 border rounded bg-gray-50 text-gray-700">
                {cityTown || 'Not specified'}
              </div>
            ) : (
            <input
              type="text"
              id="cityTown"
              value={cityTown}
              onChange={handleCityTownChange}
              className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
              placeholder="e.g., Johannesburg"
            />
            )}
          </div>
        </div>

        {/* Postal Code and Region Fields */}
        <div className="flex space-x-4 mb-6">
          {/* Postal Code */}
          <div className="flex-1">
            <label htmlFor="postalCode" className="block text-gray-700 font-bold mb-2 text-sm">
              Postal Code
            </label>
            {isViewMode ? (
              <div className="p-2 border rounded bg-gray-50 text-gray-700">
                {postalCode || 'Not specified'}
              </div>
            ) : (
            <input
              type="text"
              id="postalCode"
              value={postalCode}
              onChange={handlePostalCodeChange}
              className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
              placeholder="e.g., 2196"
            />
            )}
          </div>
          
          {/* Region */}
          <div className="flex-1">
            <label htmlFor="region" className="block text-gray-700 font-bold mb-2 text-sm">
              Region
            </label>
            {isViewMode ? (
              <div className="p-2 border rounded bg-gray-50 text-gray-700">
                {region || 'Not specified'}
              </div>
            ) : (
            <input
              type="text"
              id="region"
              value={region}
              onChange={handleRegionChange}
              className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
              placeholder="e.g., Gauteng"
            />
            )}
          </div>
        </div>

        {/* Country Field */}
        <div className="mb-6">
          <label htmlFor="country" className="block text-gray-700 font-bold mb-2 text-sm">
            Country
          </label>
          {isViewMode ? (
              <div className="p-2 border rounded bg-gray-50 text-gray-700">
                {country || 'Not specified'}
              </div>
            ) : (
          <input
            type="text"
            id="country"
            value={country}
            onChange={handleCountryChange}
            className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
            placeholder="e.g., South Africa"
          />
            )}
        </div>

        {/* Overnight Parking - Yes/No Radio (better UX than checkbox) */}
<div className="mt-6">
  <label className="block text-sm font-medium text-gray-700 mb-2">
    Are any of the vehicles parked anywhere else than at the risk address overnight?
  </label>
  <div className="flex gap-6">
    <label className="flex items-center">
      <input
        type="radio"
        name="overnightElsewhere"
        checked={overnightParkingElsewhere === true}
        onChange={() => setOvernightParkingElsewhere(true)}
        className="h-4 w-4 text-blue-600 focus:ring-blue-500"
      />
      <span className="ml-2 text-sm text-gray-700">Yes</span>
    </label>
    <label className="flex items-center">
      <input
        type="radio"
        name="overnightElsewhere"
        checked={overnightParkingElsewhere === false}
        onChange={() => {
          setOvernightParkingElsewhere(false);
          // Clear address when switching to No
          setOvernightAddress({
            address_line1: '',
            address_line2: '',
            suburb: '',
            city_town: '',
            postal_code: '',
            region: '',
            country: '',
          });
        }}
        className="h-4 w-4 text-blue-600 focus:ring-blue-500"
      />
      <span className="ml-2 text-sm text-gray-700">No</span>
    </label>
  </div>
</div>

{/* Show autocomplete + fields only when Yes */}
{overnightParkingElsewhere && (
  <div className="mt-6 p-4 border border-gray-300 rounded-lg bg-gray-50 space-y-4">
    <h4 className="text-md font-semibold text-gray-800">Overnight Parking Address</h4>

    {/* Mapbox Search Input */}
    <div className="relative">
      <label className="block text-sm font-medium text-gray-700 mb-1">
        Search Address (autocomplete)
      </label>
      <input
        ref={overnightInputRef}
        type="text"
        onChange={handleOvernightSearchChange}
        placeholder="Start typing street address..."
        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
      />

      {/* Suggestions Dropdown */}
      {showOvernightSuggestions && overnightSuggestions.length > 0 && (
        <ul className="absolute z-10 w-full bg-white border border-gray-200 rounded-md mt-1 max-h-60 overflow-auto shadow-lg">
          {overnightSuggestions.map((feature, idx) => (
            <li
              key={feature.id || idx}
              className="px-3 py-2 hover:bg-blue-50 cursor-pointer text-sm"
              onClick={() => handleOvernightAddressSelect(feature)}
            >
              {feature.place_name}
            </li>
          ))}
        </ul>
      )}
    </div>

    {/* Auto-filled fields - user can still edit */}
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Address Line 1</label>
        <input
          type="text"
          value={overnightAddress.address_line1}
          onChange={(e) => handleOvernightAddressChange('address_line1', e.target.value)}
          className="w-full px-3 py-2 border rounded-md"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Address Line 2</label>
        <input
          type="text"
          value={overnightAddress.address_line2}
          onChange={(e) => handleOvernightAddressChange('address_line2', e.target.value)}
          className="w-full px-3 py-2 border rounded-md"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Suburb</label>
        <input
          type="text"
          value={overnightAddress.suburb}
          onChange={(e) => handleOvernightAddressChange('suburb', e.target.value)}
          className="w-full px-3 py-2 border rounded-md"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">City/Town</label>
        <input
          type="text"
          value={overnightAddress.city_town}
          onChange={(e) => handleOvernightAddressChange('city_town', e.target.value)}
          className="w-full px-3 py-2 border rounded-md"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Postal Code</label>
        <input
          type="text"
          value={overnightAddress.postal_code}
          onChange={(e) => handleOvernightAddressChange('postal_code', e.target.value)}
          className="w-full px-3 py-2 border rounded-md"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Region/Province</label>
        <input
          type="text"
          value={overnightAddress.region}
          onChange={(e) => handleOvernightAddressChange('region', e.target.value)}
          className="w-full px-3 py-2 border rounded-md"
        />
      </div>

      <div className="md:col-span-2">
        <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
        <input
          type="text"
          value={overnightAddress.country}
          onChange={(e) => handleOvernightAddressChange('country', e.target.value)}
          className="w-full px-3 py-2 border rounded-md"
        />
      </div>
    </div>
  </div>
)}

        {/* Premium, Excess, and No Claim Bonus */}
        <div className="flex space-x-4 mb-6">
          {/* Premium */}
          <div className="flex-1">
            <label htmlFor="premium" className="block text-gray-700 font-bold mb-2 text-sm">
              Premium
            </label>
            {isViewMode ? (
                  <div className="p-2 border rounded bg-gray-50 text-gray-700">
                    {premium || 'Not specified'}
                  </div>
                ) : (
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <span className="text-gray-500">R</span>
              </div>
              <input
                type="text"
                id="premium"
                value={currencyDisplay.premium}
                onChange={handlePremiumChange}
                onBlur={() => handleCurrencyBlur('premium')}
                className="shadow-sm border rounded w-full py-2 px-3 pl-8 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                placeholder="0.00"
              />
            </div>
                )}
          </div>
          
          {/* Excess */}
          <div className="flex-1">
            <label htmlFor="excess" className="block text-gray-700 font-bold mb-2 text-sm">
              Excess <span className="font-bold italic text-gray-600">(Rand Value Only)</span>
            </label>
            {isViewMode ? (
                  <div className="p-2 border rounded bg-gray-50 text-gray-700">
                    {excess || 'Not specified'}
                  </div>
                ) : (
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <span className="text-gray-500">R</span>
              </div>
              <input
                type="text"
                id="excess"
                value={currencyDisplay.excess}
                onChange={handleExcessChange}
                onBlur={() => handleCurrencyBlur('excess')}
                className="shadow-sm border rounded w-full py-2 px-3 pl-8 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                placeholder="0.00"
              />
            </div>
                )}
          </div>
          
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end mt-6 space-x-4">
          <button
            onClick={onClose}
            className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded-lg shadow-md transition duration-300"
          >
            {isViewMode ? 'Close' : 'Cancel'}
          </button>

          {!isViewMode && (
            <button
              onClick={handleSaveClick}
              disabled={isSaving}
              className={`bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-lg shadow-md transition duration-300 ${
                isSaving ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {isSaving ? (
                <>
                  <span className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></span>
                  Saving...
                </>
              ) : (
                vehicle?.id ? 'Update Vehicle' : 'Add Vehicle'
              )}
            </button>
          )}

        </div>
      </div>
    </div>
  );
};

export const EditableVehicleCard: React.FC<EditableVehicleCardProps> = ({ 
  vehicle, 
  index, 
  onEdit,
  cardNumber,
  cardTitle = 'Vehicle',
  showCardNumber = true
}) => {
  // Format currency for display
  const formatCurrency = (value: number | string): string => {
    if (!value) return '';
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    return isNaN(numValue) ? '' : `R ${numValue.toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Format vehicle info for display
  const formatVehicleInfo = (): string => {
    const vehicleParts = [
      vehicle.make,
      vehicle.model,
      vehicle.year ? `(${vehicle.year})` : '',
      vehicle.variant ? `- ${vehicle.variant}` : ''
    ].filter(part => part && part.trim() !== '');
    return vehicleParts.join(' ');
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200">
      <div className="p-4">
        <div className="flex justify-between items-start mb-2">
          <h4 className="font-semibold text-purple-600">
            {showCardNumber ? `${cardTitle} #${cardNumber || (index + 1)}` : cardTitle}
          </h4>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => onEdit(index)}
              className="text-blue-600 hover:text-blue-800 text-sm font-medium"
            >
              Edit
            </button>
          </div>
        </div>
        
        <div className="space-y-2 text-sm">
          {/* Display Mode */}
          <div className="flex justify-between">
            <span className="text-gray-600">Vehicle:</span>
            <span className="font-medium text-right max-w-[150px]">
              {formatVehicleInfo() || 'No vehicle info'}
            </span>
          </div>
          
          <div className="flex justify-between">
            <span className="text-gray-600">Registration:</span>
            <span className="font-medium">{vehicle.registration_number || 'Not set'}</span>
          </div>

          <div className="flex justify-between">
            <span className="text-gray-600">VIN:</span>
            <span className="font-medium">{vehicle.vin_number || 'Not set'}</span>
          </div>
          
          <div className="flex justify-between">
            <span className="text-gray-600">Color:</span>
            <span className="font-medium">{vehicle.color || 'Not set'}</span>
          </div>
          
          <div className="flex justify-between">
            <span className="text-gray-600">Insured Value:</span>
            <span className="font-medium text-green-600">
              {formatCurrency(vehicle.insured_value || 0) || 'Not set'}
            </span>
          </div>
          
          <div className="flex justify-between">
            <span className="text-gray-600">Premium:</span>
            <span className="font-medium text-purple-600">
              {formatCurrency(vehicle.premium || 0) || 'Not set'}
            </span>
          </div>

          <div className="flex justify-between">
            <span className="text-gray-600">Excess:</span>
            <span className="font-medium text-blue-600">
              {formatCurrency(vehicle.excess || 0) || 'Not set'}
            </span>
          </div>

          {vehicle.cover_type && (
            <div className="flex justify-between">
              <span className="text-gray-600">Cover Type:</span>
              <span className="font-medium">{vehicle.cover_type}</span>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default AddVehicleModal;